import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Ban, BadgeCheck, ChevronLeft, ShieldCheck, Snowflake } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { PERMISSIONS } from '@/lib/permissions'
import { InfoRow, MonoValue } from '@/components/DataDisplay'
import { StatusBadge, Tag } from '@/components/StatusBadge'
import { ErrorState, PermissionDenied, Skeleton } from '@/components/States'
import { Dialog } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Field as FormField, Input } from '@/components/ui/input'
import { DangerDialog } from '@/components/DangerDialog'
import { useAuth } from '@/providers/AuthProvider'
import { useToast } from '@/providers/UiProviders'
import {
  grantBotVerifier,
  revokeBotVerifier,
  banUser,
  unbanUser,
  freezeUser,
  unfreezeUser,
} from '@/lib/adapters'
import { fetchBotConfig, type AdminBotConfig } from './botApi'
import { BotOwnerSection, BotTokenSection } from './BotSections'

/**
 * Bot card - `GET /admin/bots/:id`. Only real mutation available: grant/revoke "org
 * verifier" status (BotAggregate.GrantVerifierStatus/RevokeVerifierStatus - lets the bot
 * itself place a "verified by X" badge on other accounts via bots.setCustomVerification,
 * which already works end-to-end). Edit/official-badge/enable-disable/block/delete still
 * have no admin-override path in the backend (owner-gated self-service only) - stay
 * view-only there rather than fabricate an action the server can't honor.
 */
export function BotCard() {
  const t = useT()
  const { can } = useAuth()
  const { toast } = useToast()
  const { id = '' } = useParams()
  const [bot, setBot] = useState<AdminBotConfig | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)
  const [grantOpen, setGrantOpen] = useState(false)
  const [banOpen, setBanOpen] = useState(false)
  const [freezeOpen, setFreezeOpen] = useState(false)
  const companyRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)
    fetchBotConfig(Number(id), ac.signal)
      .then((b) => {
        if (alive) setBot(b)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [id, reload])

  if (!can(PERMISSIONS.USERS_VIEW)) return <PermissionDenied permission={PERMISSIONS.USERS_VIEW} />

  const canModerate = can(PERMISSIONS.USERS_MODERATE)

  const runSimple = async (fn: () => Promise<unknown>) => {
    try {
      await fn()
      toast(t.users.actionDone, { tone: 'success' })
      setReload((n) => n + 1)
    } catch (e) {
      toast(t.users.actionFailed, {
        tone: 'error',
        ...(e instanceof Error && e.message ? { description: e.message } : {}),
      })
    }
  }

  const revoke = async () => {
    try {
      await revokeBotVerifier(Number(id))
      toast(t.users.actionDone, { tone: 'success' })
      setReload((n) => n + 1)
    } catch (e) {
      toast(t.users.actionFailed, {
        tone: 'error',
        ...(e instanceof Error && e.message ? { description: e.message } : {}),
      })
    }
  }

  const actions = (
    <div className="flex flex-wrap items-center gap-1.5">
      {canModerate && bot && (
        <>
          {bot.is_banned ? (
            <button
              type="button"
              onClick={() => void runSimple(() => unbanUser(bot.id))}
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
            >
              <ShieldCheck size={13} />
              {t.users.unban}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setBanOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-md border border-danger bg-danger-subtle px-2.5 py-1 text-sm text-danger hover:border-danger"
            >
              <Ban size={13} />
              {t.users.ban}
            </button>
          )}
          {bot.is_frozen ? (
            <button
              type="button"
              onClick={() => void runSimple(() => unfreezeUser(bot.id))}
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
            >
              <ShieldCheck size={13} />
              {t.users.unfreeze}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setFreezeOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-md border border-warning bg-warning-subtle px-2.5 py-1 text-sm text-warning hover:border-warning"
            >
              <Snowflake size={13} />
              {t.users.freeze}
            </button>
          )}
          <button
            type="button"
            onClick={() => (bot.is_official ? void revoke() : setGrantOpen(true))}
            className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
          >
            <BadgeCheck size={13} />
            {bot.is_official ? t.bots.verifierRevoke : t.bots.verifierGrant}
          </button>
        </>
      )}
      <Link
        to="/bots"
        className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
      >
        <ChevronLeft size={13} />
        {t.nav.bots}
      </Link>
    </div>
  )

  return (
    <>
      <PageHeader
        title={bot ? bot.first_name || (bot.username ? `@${bot.username}` : `#${bot.id}`) : t.bots.card}
        screenId="A5.02"
        actions={actions}
      />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {loading ? (
          <div className="max-w-xl space-y-2 rounded-lg border border-border-default bg-surface-2 p-4">
            {Array.from({ length: 6 }, (_, i) => (
              <Skeleton key={i} className="h-4 w-2/3" />
            ))}
          </div>
        ) : error ? (
          <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
        ) : !bot ? (
          <ErrorState title={t.states.notFoundTitle} />
        ) : (
          <>
            <div className="max-w-xl rounded-lg border border-border-default bg-surface-2 p-4">
              <div className="mb-3 flex items-center gap-2">
                <StatusBadge status={bot.revoked || bot.is_banned ? 'banned' : 'active'} />
                <Tag>bot</Tag>
                {bot.is_official && <Tag>official</Tag>}
              </div>
              <Field label={t.col.id}>
                <MonoValue value={String(bot.id)} />
              </Field>
              <Field label={t.col.name}>{bot.first_name || '—'}</Field>
              <Field label={t.col.username}>{bot.username ? `@${bot.username}` : '—'}</Field>
              <Field label={t.bots.official}>{bot.is_official ? '✓' : '—'}</Field>
              <Field label={t.bots.statusLabel}>{bot.revoked ? t.bots.disabled : t.bots.active}</Field>
              {bot.is_banned && (
                <Field label={t.users.bannedReason}>
                  <span className="text-danger">{bot.ban_reason || '—'}</span>
                </Field>
              )}
              {bot.is_frozen && (
                <Field label={t.users.freezeReason}>
                  <span className="text-warning">{bot.freeze_reason || '—'}</span>
                </Field>
              )}
              <Field label={t.bots.descLabel}>{bot.description || '—'}</Field>
              <Field label={t.bots.aboutLabel}>{bot.about || '—'}</Field>
              <Field label={t.bots.commandsLabel}>
                {bot.commands.length ? (
                  <div className="space-y-1">
                    {bot.commands.map((c) => (
                      <div key={c.command} className="flex gap-2 text-sm">
                        <code className="text-accent">/{c.command}</code>
                        <span className="text-secondary">{c.description}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  t.bots.noCommands
                )}
              </Field>
            </div>

            <BotOwnerSection botId={bot.id} />
            <BotTokenSection botId={bot.id} />
          </>
        )}
      </div>

      {grantOpen && bot && (
        <GrantVerifierDialog
          botId={bot.id}
          onClose={() => setGrantOpen(false)}
          onGranted={() => {
            setGrantOpen(false)
            setReload((n) => n + 1)
          }}
          initialFocusRef={companyRef}
          companyRef={companyRef}
        />
      )}

      {banOpen && bot && (
        <DangerDialog
          open
          onClose={() => setBanOpen(false)}
          screenId="A5.02"
          level={2}
          title={t.users.banTitle(bot.first_name || (bot.username ? `@${bot.username}` : `#${bot.id}`))}
          description={t.users.banDesc}
          confirmLabel={t.users.ban}
          onConfirm={async ({ reason }) => {
            try {
              await banUser(bot.id, reason)
              toast(t.users.actionDone, { tone: 'success' })
              setReload((n) => n + 1)
            } catch (e) {
              toast(t.users.actionFailed, {
                tone: 'error',
                ...(e instanceof Error && e.message ? { description: e.message } : {}),
              })
              throw e
            }
          }}
        />
      )}

      {freezeOpen && bot && (
        <DangerDialog
          open
          onClose={() => setFreezeOpen(false)}
          screenId="A5.02"
          level={2}
          title={t.users.freezeTitle(bot.first_name || (bot.username ? `@${bot.username}` : `#${bot.id}`))}
          description={t.users.freezeDesc}
          confirmLabel={t.users.freeze}
          onConfirm={async ({ reason }) => {
            try {
              await freezeUser(bot.id, reason)
              toast(t.users.actionDone, { tone: 'success' })
              setReload((n) => n + 1)
            } catch (e) {
              toast(t.users.actionFailed, {
                tone: 'error',
                ...(e instanceof Error && e.message ? { description: e.message } : {}),
              })
              throw e
            }
          }}
        />
      )}
    </>
  )
}

function GrantVerifierDialog({
  botId,
  onClose,
  onGranted,
  initialFocusRef,
  companyRef,
}: {
  botId: number
  onClose: () => void
  onGranted: () => void
  initialFocusRef: React.RefObject<HTMLElement>
  companyRef: React.RefObject<HTMLInputElement>
}) {
  const t = useT()
  const { toast } = useToast()
  const [icon, setIcon] = useState('')
  const [company, setCompany] = useState('')
  const [description, setDescription] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const submit = async () => {
    const iconNum = Number(icon)
    if (!company.trim() || !Number.isFinite(iconNum) || iconNum <= 0) return
    setSubmitting(true)
    try {
      await grantBotVerifier(botId, {
        icon: iconNum,
        company: company.trim(),
        ...(description.trim() ? { custom_description: description.trim() } : {}),
      })
      toast(t.users.actionDone, { tone: 'success' })
      onGranted()
    } catch (e) {
      toast(t.users.actionFailed, {
        tone: 'error',
        ...(e instanceof Error && e.message ? { description: e.message } : {}),
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title={t.bots.verifierGrantTitle}
      description={t.bots.verifierGrantDesc}
      initialFocusRef={initialFocusRef}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t.common.cancel}
          </Button>
          <Button variant="primary" onClick={() => void submit()} disabled={submitting}>
            {t.bots.verifierGrant}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <FormField label={t.bots.verifierCompany} required>
          <Input ref={companyRef} value={company} onChange={(e) => setCompany(e.target.value)} />
        </FormField>
        <FormField label={t.bots.verifierIcon} required>
          <Input value={icon} onChange={(e) => setIcon(e.target.value)} inputMode="numeric" />
        </FormField>
        <FormField label={t.bots.verifierDescription}>
          <Input value={description} onChange={(e) => setDescription(e.target.value)} />
        </FormField>
      </div>
    </Dialog>
  )
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return <InfoRow label={label}>{children}</InfoRow>
}
